import React, { useState, useEffect } from 'react';
import { Card, ListGroup, Badge, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../../config/api';
import StatusBadge from '../shared/StatusBadge';

export default function PriorityTasksWidget() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { apiCall } = useAuth();
  const navigate = useNavigate();

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await apiCall(`${API_BASE_URL}/tasks/due-today`); // Still using same endpoint
      setTasks(response.data || []);
    } catch (error) {
      console.error('Failed to load pending tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await apiCall(`${API_BASE_URL}/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      await loadTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleNavigateToEntity = (task) => {
    if (task.customer_id) {
      navigate(`/dashboard/customers/${task.customer_id}`);
    } else if (task.vehicle_id) {
      navigate(`/dashboard/vehicles/${task.vehicle_id}`);
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="text-white d-flex justify-content-between align-items-center" style={{ backgroundColor: '#10b981' }}>
        <div>
          <i className="fas fa-tasks me-2"></i>
          Pending Tasks
          {tasks.length > 0 && (
            <Badge bg="light" text="dark" className="ms-2">{tasks.length}</Badge>
          )}
        </div>
        <Button
          variant="light"
          size="sm"
          onClick={loadTasks}
          disabled={loading}
        >
          <i className="fas fa-sync-alt"></i>
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center text-muted p-4">
            <i className="fas fa-check-double fa-2x mb-2"></i>
            <p className="mb-0">No pending tasks</p>
          </div>
        ) : (
          <ListGroup variant="flush">
            {tasks.map((task) => (
              <ListGroup.Item key={task.id}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="flex-grow-1">
                    <StatusBadge status={task.priority} type="task" className="me-2" />
                    <span className="fw-bold">{task.title}</span>
                  </div>
                  <Badge bg={task.status === 'doing' ? 'primary' : 'secondary'}>
                    {task.status}
                  </Badge>
                </div>

                {task.description && (
                  <p className="text-muted small mb-2">{task.description}</p>
                )}

                <div className="d-flex gap-2 flex-wrap mb-2">
                  {task.assigned_agent_name && (
                    <small className="text-muted">
                      <i className="fas fa-user me-1"></i>
                      {task.assigned_agent_name}
                    </small>
                  )}

                  {(task.customer_id || task.vehicle_id) && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => handleNavigateToEntity(task)}
                    >
                      {task.customer_id && `${task.first_name} ${task.last_name}`}
                      {task.vehicle_id && `${task.year} ${task.make} ${task.model}`}
                      <i className="fas fa-arrow-right ms-1"></i>
                    </Button>
                  )}
                </div>

                <div className="mt-2">
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => handleStatusChange(task.id, 'done')}
                    disabled={task.status === 'done'}
                  >
                    <i className="fas fa-check me-1"></i>
                    Done
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Card.Body>
    </Card>
  );
}
